import React, { useEffect, useRef, useState } from "react";
import { Empty } from "./OverviewTab";
import { useT, useI18n } from "../../lib/i18n";
import { api } from "../../lib/api";
import { Sparkle, WarningOctagon, FileText } from "@phosphor-icons/react";

const P_COLORS = { P0: "chip-III", P1: "chip-IIb", P2: "chip-IIa" };

export default function CoachTab({ project, version }) {
  const t = useT();
  const { lang } = useI18n();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [planState, setPlanState] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planFailed, setPlanFailed] = useState(false);
  const requestIdRef = useRef(0);
  const bottomRef = useRef(null);
  const scope = JSON.stringify([project?.id, version?.device_id, version?.version]);
  const plan = planState?.scope === scope ? planState.data : null;
  const busy = sending || planLoading;

  useEffect(() => {
    requestIdRef.current += 1;
    setMessages([]);
    setDraft("");
    setSending(false);
    setPlanState(null);
    setPlanFailed(false);
    return () => {
      requestIdRef.current += 1;
    };
  }, [scope]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ behavior: "smooth", block: "end" });
  }, [messages, sending]);

  if (!version) return <Empty msg={t("ov.no_analysis")} />;

  async function sendMessage(rawMessage = draft, retryFailed = false) {
    const message = (rawMessage || "").trim();
    if (!message || busy) return;

    const requestId = ++requestIdRef.current;
    const cleanMessages = messages.filter((item) => !item.isError);
    let historyMessages = cleanMessages;
    let visibleMessages = cleanMessages;
    let outgoingMessage = message;

    if (retryFailed) {
      const previousUserMessage = [...cleanMessages].reverse().find((item) => item.role === "user");
      if (!previousUserMessage) return;
      outgoingMessage = previousUserMessage.content;
      historyMessages = cleanMessages.filter((item) => item.id !== previousUserMessage.id);
    } else {
      const userMessage = { id: `${requestId}-user`, role: "user", content: message };
      visibleMessages = [...cleanMessages, userMessage];
      setDraft("");
    }

    setMessages(visibleMessages);
    setSending(true);
    try {
      const { data } = await api.post(
        `/projects/${project.id}/versions/${version.version}/coach/chat`,
        {
          message: outgoingMessage,
          history: historyMessages.slice(-10).map(({ role, content }) => ({ role, content })),
          lang,
        },
        { params: version.device_id ? { device_id: version.device_id } : {} },
      );
      if (requestId === requestIdRef.current) {
        setMessages((current) => [...current, {
          id: `${requestId}-assistant`,
          role: "assistant",
          content: data?.reply || t("coach.reply_unavailable"),
          isError: Boolean(data?.error),
        }]);
      }
    } catch {
      if (requestId === requestIdRef.current) {
        setMessages((current) => [...current, {
          id: `${requestId}-assistant-error`,
          role: "assistant",
          content: t("coach.reply_unavailable"),
          isError: true,
        }]);
      }
    } finally {
      if (requestId === requestIdRef.current) setSending(false);
    }
  }

  async function generateActionPlan() {
    if (busy) return;
    const requestId = ++requestIdRef.current;
    setPlanLoading(true);
    setPlanFailed(false);
    const conversation = messages
      .filter((item) => !item.isError)
      .slice(-10)
      .map((item) => `${item.role === "user" ? t("coach.you") : t("coach.name")}: ${item.content}`)
      .join("\n")
      .slice(-4000);
    try {
      const { data } = await api.post(
        `/projects/${project.id}/versions/${version.version}/coach`,
        { context: conversation, lang },
        { params: version.device_id ? { device_id: version.device_id } : {} },
      );
      if (requestId === requestIdRef.current) setPlanState({ scope, data });
    } catch {
      if (requestId === requestIdRef.current) setPlanFailed(true);
    } finally {
      if (requestId === requestIdRef.current) setPlanLoading(false);
    }
  }

  function handleSubmit(event) {
    event.preventDefault();
    sendMessage();
  }

  return (
    <div className="space-y-5 text-[var(--text)]" data-testid="coach-tab">
      <header className="grid-card p-5">
        <div className="text-xs font-mono uppercase tracking-wider text-[var(--text-2)]">{t("coach.title")}</div>
        <p className="mt-1 text-sm text-[var(--text-2)]">{t("coach.sub")}</p>
        <p className="mt-2 text-xs text-[var(--text-muted)]">{t("coach.disclaimer")}</p>
      </header>

      <section
        className="grid-card min-h-72 space-y-4 p-4 sm:p-5"
        aria-live="polite"
        aria-label={t("coach.conversation")}
        data-testid="coach-conversation"
      >
        {messages.length === 0 && (
          <div className="max-w-2xl py-8 text-sm text-[var(--text-2)]" data-testid="coach-welcome">
            {t("coach.welcome")}
          </div>
        )}
        {messages.map((item) => (
          <div
            key={item.id}
            className={`max-w-full rounded-sm border p-3 text-sm sm:max-w-[88%] ${
              item.role === "user"
                ? "ml-auto border-[var(--border)] bg-[var(--surface-elevated)]"
                : item.isError
                  ? "border-[var(--warning)] bg-[var(--surface)] text-[var(--risk-warning)]"
                  : "mr-auto border-[var(--border)] bg-[var(--surface)]"
            }`}
            data-testid={item.isError ? "coach-message-error" : `coach-message-${item.role}`}
          >
            <div className="mb-1 text-[10px] font-mono uppercase tracking-wide text-[var(--text-2)]">
              {item.role === "user" ? t("coach.you") : t("coach.name")}
            </div>
            <p className="whitespace-pre-wrap break-words">{item.content}</p>
            {item.isError && (
              <button
                type="button"
                className="mt-3 rounded-sm border border-[var(--border)] px-3 py-1.5 text-xs text-[var(--text)] hover:border-[var(--accent)]"
                onClick={() => sendMessage(item.content, true)}
                disabled={busy}
                data-testid="coach-chat-retry"
              >
                {t("coach.retry_reply")}
              </button>
            )}
          </div>
        ))}
        {sending && (
          <div className="mr-auto text-xs text-[var(--text-2)]" role="status" data-testid="coach-chat-loading">
            {t("coach.running")}
          </div>
        )}
        <div ref={bottomRef} />
      </section>

      <form onSubmit={handleSubmit} className="grid-card p-3 sm:p-4" data-testid="coach-chat-form">
        <label className="sr-only" htmlFor="coach-chat-input">{t("coach.input_label")}</label>
        <textarea
          id="coach-chat-input"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              sendMessage();
            }
          }}
          rows={3}
          maxLength={2000}
          placeholder={t("coach.placeholder")}
          className="w-full resize-y rounded-sm border border-[var(--border)] bg-[var(--surface-elevated)] px-3 py-2 text-sm text-[var(--text)] placeholder:text-[var(--text-muted)] focus:border-[var(--accent)] focus:outline-none"
          data-testid="coach-chat-input"
        />
        <div className="mt-3 flex flex-col gap-2 border-t border-[var(--border)] pt-3 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={generateActionPlan}
            disabled={busy}
            className="inline-flex min-h-10 items-center justify-center gap-2 self-start rounded-sm border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-xs text-[var(--text)] hover:border-[var(--accent)] disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="coach-plan-btn"
          >
            <Sparkle size={14} weight="fill" />
            {planLoading ? t("coach.plan_running") : t("coach.plan_optional")}
          </button>
          <button
            type="submit"
            disabled={busy || !draft.trim()}
            className="btn-primary inline-flex min-h-10 items-center justify-center gap-2 self-end px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="coach-send-btn"
          >
            {t("coach.send")}
          </button>
        </div>
      </form>

      {planFailed && (
        <div className="grid-card border-l-4 border-l-[var(--warning)] p-4 text-sm text-[var(--risk-warning)]" role="alert" data-testid="coach-plan-error">
          <span className="inline-flex items-center gap-2"><WarningOctagon size={14} weight="fill" /> {t("coach.request_error")}</span>
        </div>
      )}

      {plan && (
        <div className="space-y-4" data-testid="coach-plan">
          {(plan.alerts || []).length > 0 && (
            <div className="grid-card border-l-4 border-l-[var(--risk-danger)] p-5">
              <div className="mb-2 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-[var(--risk-danger)]"><WarningOctagon size={12} weight="fill" /> {t("coach.alerts")}</div>
              <ul className="space-y-1 text-sm">{plan.alerts.map((alert, index) => <li key={`${alert}-${index}`}>· {alert}</li>)}</ul>
            </div>
          )}

          {(plan.actions || []).length > 0 && (
            <div className="grid-card overflow-hidden p-0">
              <div className="border-b border-[var(--border)] px-5 py-3 text-xs font-mono uppercase tracking-wider text-[var(--text-2)]">{t("coach.actions")}</div>
              <table className="w-full text-sm">
                <tbody>
                  {plan.actions.map((action, index) => (
                    <tr key={action.title || index} className={`risk-table-row ${index % 2 === 1 ? "risk-table-row-alt" : ""} border-b border-[var(--border)] last:border-0`} data-testid={`coach-action-${index}`}>
                      <td className="w-20 px-4 py-3 align-top"><span className={`chip ${P_COLORS[action.priority] || "chip-IIa"}`}>{action.priority || "P2"}</span></td>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{action.title}</div>
                        {action.why && <div className="mt-1 text-xs text-[var(--text-2)]">{action.why}</div>}
                        <div className="mt-1 flex items-center gap-3 text-[10px] font-mono text-[var(--text-2)]">
                          {action.deadline_hint && <span>{t("coach.deadline")} {action.deadline_hint}</span>}
                          {action.mdr_ref && <span>· {action.mdr_ref}</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(plan.drafts || []).length > 0 && (
            <div className="grid-card p-5">
              <div className="mb-3 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-[var(--text-2)]"><FileText size={12} /> {t("coach.drafts")}</div>
              <div className="space-y-3">
                {plan.drafts.map((item, index) => (
                  <div key={item.title || index} className="rounded-sm border border-[var(--border)] bg-[var(--surface-elevated)] p-3">
                    <div className="text-sm font-semibold">{item.title}</div>
                    <p className="mt-1 whitespace-pre-wrap text-xs text-[var(--text-2)]">{item.outline}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
